<template>
  <div class="suppliers" v-if="getSuppliers">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getSuppliers"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getSupplierCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('all_suppliers') }}</h4>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>

            <button class="btn btn-sm btn-inverse" @click="$emit('create-supplier')">
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('supplier_create') }}
            </button>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <!-- Supplier Number -->
      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '60px' }"
        :bodyStyle="'text-align: center; width: 60px'"
        filterField="number"
      >
        <template #body="slotProps">
          <router-link
            v-if="slotProps.data && slotProps.data.number !== null"
            :to="{
              name: 'SupplierEditPage',
              params: { supplierNumber: slotProps.data.number },
            }"
          >
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </router-link>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Supplier Name -->
      <Column
        field="name"
        :header="$t('supplier')"
        sortable
        filterMatchMode="contains"
        filterField="name"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.name">
            {{ slotProps.data.name }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Created At -->
      <Column
        field="createdAt"
        :header="$t('createdAt')"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        filterField="createdAtDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.createdAt">
            {{ formatDate(slotProps.data.createdAt) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Updated At -->
      <Column
        field="updatedAt"
        :header="$t('updatedAt')"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        filterField="updatedAtDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.updatedAt">
            {{ formatDate(slotProps.data.updatedAt) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Actions -->
      <Column
        :header="$t('actions')"
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center; width: 100px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <button class="btn" @click="$emit('edit-supplier', slotProps.data)" title="Edit Supplier">
            <i class="link-icon fa fa-lg fa-edit"></i>
          </button>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import dayjs from 'dayjs';
import Calendar from 'primevue/calendar';

export default {
  name: 'SuppliersTable',
  components: {
    DataTable,
    InputText,
    Column,
    Calendar,
  },

  props: {
    tableStatePrefix: {
      type: String,
      default: 'suppliers',
    },
  },

  data() {
    return {
      supplierTableStateConfig: {
        name: this.tableStatePrefix,
        filters: {
          number: { value: null },
          name: { value: null },
          createdAtDateRange: { range: null },
          updatedAtDateRange: { range: null },
        },
        sorting: { sortField: 'number', sortOrder: -1 }, // Latest suppliers first
      },
      defaultFilters: {
        number: { value: null },
        name: { value: null },
        createdAtDateRange: { range: null },
        updatedAtDateRange: { range: null },
      },
    };
  },

  computed: {
    ...mapGetters(['getSuppliers', 'getSupplierCount']),
    ...mapGetters(['isLoading', 'getTableState']),

    tableStateStore() {
      return this.getTableState(this.supplierTableStateConfig.name);
    },
  },

  methods: {
    ...mapActions(['fetchSuppliersPaginated']),
    ...mapActions(['initTableState']),

    /**
     * Load remote table data
     */
    async loadTableData() {
      const filters = { ...this.tableStateStore.filters };

      await this.fetchSuppliersPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: filters,
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /**
     * Format date for display
     */
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('DD.MM.YYYY');
    },

    /**
     * Action handlers
     */
    viewSupplier(supplier) {
      this.$router.push({
        name: 'SupplierEditPage',
        params: { supplierNumber: supplier.number },
      });
    },

    editSupplier(supplier) {
      this.$router.push({
        name: 'SupplierEditPage',
        params: { supplierNumber: supplier.number },
      });
    },

    /**
     * Reset filter settings
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
  },

  async created() {
    await this.initTableState(this.supplierTableStateConfig);
  },

  async mounted() {
    await this.loadTableData();
  },
};
</script>

<style lang="scss">
.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  justify-content: flex-start;
}

.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
